import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import logoAtelier from "@/assets/images/logo-atelierdupendu.webp";
import arrierePlanSite from "@/assets/images/arriere-plan-site.webp";

const Hero = () => {
  const shouldReduceMotion = useReducedMotion();

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
      };

  const fadeLeft = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: -30 },
        animate: { opacity: 1, x: 0 },
      };

  const fadeScale = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.96 },
        animate: { opacity: 1, scale: 1 },
      };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20">
      <div className="absolute inset-0 z-0">
        <img
          src={logoAtelier}
          alt="Sellerie artisanale"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          sizes="100vw"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/60 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            {...fadeLeft}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <motion.p
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              animate={shouldReduceMotion ? undefined : { opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="text-primary-light font-courier text-sm md:text-base uppercase tracking-widest mb-4"
            >
              Saint-Cergues • Haute-Savoie
            </motion.p>

            <motion.h1
              {...fadeUp}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              L'Art du Cuir
              <br />
              <span className="italic text-primary-light">& de la Sellerie</span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="font-manrope text-lg md:text-xl mb-8 text-secondary-main leading-relaxed max-w-xl"
            >
              Le confort et l'esthétisme sont les deux piliers d'une sellerie de qualité.
              Notre engagement pour chaque projet est total pour vous assurer une finition à la hauteur de vos attentes.
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={scrollToContact}
                className="group bg-primary-main text-white px-8 py-4 rounded-full hover:bg-primary-dark transition-all duration-300 hover:-translate-y-1 shadow-xl font-manrope font-semibold text-lg flex items-center justify-center gap-2"
                data-testid="hero-contact-btn"
              >
                Demander un devis
                <ArrowRight
                  className="group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              </button>

              <a
                href="tel:0673567675"
                className="border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white hover:text-primary-main transition-all duration-300 font-manrope font-semibold text-lg text-center"
                data-testid="hero-phone-btn"
              >
                06 73 56 76 75
              </a>
            </motion.div>

            <motion.div
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              animate={shouldReduceMotion ? undefined : { opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-12 flex items-center gap-8"
            >
              <div>
                <p className="text-3xl font-playfair font-bold text-primary-light">15+</p>
                <p className="text-secondary-dark font-manrope text-sm">Ans d'expérience</p>
              </div>
              <div className="h-12 w-px bg-white/30"></div>
              <div>
                <p className="text-3xl font-playfair font-bold text-primary-light">500+</p>
                <p className="text-secondary-dark font-manrope text-sm">Projets réalisés</p>
              </div>
              <div className="h-12 w-px bg-white/30"></div>
              <div>
                <p className="text-3xl font-playfair font-bold text-primary-light">4.6/5</p>
                <p className="text-secondary-dark font-manrope text-sm">Note Google</p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            {...fadeScale}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="hidden lg:block relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={arrierePlanSite}
                alt="Artisan sellier au travail"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width="900"
                height="600"
                sizes="(max-width: 1024px) 100vw, 50vw"
                draggable={false}
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={shouldReduceMotion ? undefined : { opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className={shouldReduceMotion ? "" : "animate-bounce"}>
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;